import { usePublicGuestRsvp } from '@bridebook/web/app-basic/lib/rsvp/hooks/use-public-guest-rsvp';
import { useUpdatePublicGuestRsvp } from '@bridebook/web/app-basic/lib/rsvp/hooks/use-update-public-guest-rsvp';
import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import debounce from 'lodash/debounce';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IWedding_Website } from '@bridebook/models/source/models/Weddings.types';
import { IGuest_Invitation, Rsvp } from '@bridebook/models/source/models/Weddings/Guests.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { GetWeddingWebsiteDataResponseSuccess } from '@bridebook/web/pages/api/for/website/[websiteId]';
import { GetRsvpDataResponse } from '@bridebook/web/pages/api/for/website/[websiteId]/rsvp/guest/get';
import { SaveGuestRsvpResponse } from '@bridebook/web/pages/api/for/website/[websiteId]/rsvp/guest/save';
import { NAME_FORM_VALUE_DEBOUNCE_TIME } from '@bridebook/web/lib/rsvp/constants';
import {
  IEventFormFields,
  INameFormFields,
  IQuestionsFormFields,
  TGuestRSVPStep,
  TGuestRsvpSubmissionStatus,
} from '@bridebook/web/lib/rsvp/types';
import {
  getGuestRsvpNameValidationSchema,
  getGuestRsvpQuestionValidator,
  validateGuestRsvpEventAttendance,
} from '@bridebook/web/lib/rsvp/validation';
import { getEnrichedEvents } from '@bridebook/web/lib/rsvp/utils/get-enriched-events';
import GuestRsvpMainComponent from './guest-rsvp-main-component';
import {
  RsvpAnalyticsProvider,
  RsvpDataProvider,
  RsvpFormsProvider,
  RsvpNavigationProvider,
  RsvpStateProvider,
} from './guest-rsvp.context';
import { useDispatch } from 'react-redux';

// Guest RSVP component for live mode (wedding website subpage)
interface GuestRsvpProps {
  websiteData: GetWeddingWebsiteDataResponseSuccess;
  theme: IWedding_Website['config']['theme'];
}

const GuestRsvp = ({ websiteData, theme }: GuestRsvpProps) => {
  const { t } = useTranslation('weddingWebsite');
  const dispatch = useDispatch();

  // Get websiteId, and full name or guest ID from the router query
  const router = useRouter();

  const {
    websiteId = '',
    guest: fullName = '',
    guestId = '',
  } = router.query as {
    websiteId?: string;
    guest?: string;
    guestId?: string;
  };

  const [step, setStep] = useState<TGuestRSVPStep>('NAME_FORM');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [rsvpSubmissionStatus, setRsvpSubmissionStatus] =
    useState<TGuestRsvpSubmissionStatus>('NOT_RESOLVED');

  // Name form schema and form setup
  const nameFormSchema = getGuestRsvpNameValidationSchema(t);

  const nameForm = useForm<INameFormFields>({
    resolver: zodResolver(nameFormSchema),
    mode: 'onTouched',
    defaultValues: {
      fullName,
    },
  });

  const currentFullName = nameForm.watch('fullName');

  const [debouncedFullName, setDebouncedFullName] = useState(currentFullName);

  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      setDebouncedFullName(currentFullName);
    }, NAME_FORM_VALUE_DEBOUNCE_TIME);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [currentFullName]);

  const { data, isLoading, isFetching } = usePublicGuestRsvp(
    websiteId,
    guestId
      ? { type: 'id', id: guestId }
      : debouncedFullName
        ? { type: 'name', name: debouncedFullName }
        : undefined,
    {
      keepPreviousData: true,
    },
  );

  const dbGuestName = data?.resultCode === 'ok' ? data.guest.name : undefined;

  useEffect(() => {
    // prevents running the refetching mechanism if lowercase search found the guest
    if (dbGuestName && dbGuestName.toLowerCase() !== nameForm.getValues('fullName').toLowerCase()) {
      nameForm.setValue('fullName', dbGuestName);
    }
  }, [dbGuestName, nameForm]);

  const { mutateAsync: updateGuestRsvp } = useUpdatePublicGuestRsvp(websiteId);

  const error = useMemo(() => {
    if (data?.resultCode === 'error') {
      return (data.error.message as string) || 'error';
    }
    return null;
  }, [data]);

  const guestNotFound = data?.resultCode === 'not_found';

  const guestNoEvents = useMemo(() => {
    if (data?.resultCode !== 'ok') return false;
    const events = data.guest.invitation?.events;
    return !events || Object.keys(events).length === 0;
  }, [data]);

  const weddingRsvp = useMemo(
    () => (data?.resultCode === 'ok' && (data as GetRsvpDataResponse).wedding.rsvp) || undefined,
    [data],
  );

  const guestInvitation = useMemo(
    () =>
      (data?.resultCode === 'ok' && (data as GetRsvpDataResponse).guest.invitation) || undefined,
    [data],
  );

  const guestIdFromResponse = useMemo(
    () => (data?.resultCode === 'ok' && (data as GetRsvpDataResponse).guest.id) || undefined,
    [data],
  );

  const weddingEvents = useMemo(
    () => (data?.resultCode === 'ok' && (data as GetRsvpDataResponse).wedding.events) || [],
    [data],
  );

  const relatedGuests = useMemo<GetRsvpDataResponse['relatedGuests']>(
    () => (data?.resultCode === 'ok' ? (data as GetRsvpDataResponse).relatedGuests : []),
    [data],
  );

  const isRsvpOpen = useMemo(() => {
    if (data?.resultCode !== 'ok') return true;
    const isOpen = (data as GetRsvpDataResponse).wedding.isOpen;
    return isOpen !== undefined ? isOpen : true;
  }, [data]);

  // Loop through events attendance and check if the RSVP has already been submitted
  useEffect(() => {
    if (guestInvitation && Object.values(guestInvitation.events).length > 0) {
      const hasPendingResponse = Object.values(guestInvitation.events).some(
        (event) => event.rsvp === 'pending',
      );
      setRsvpSubmissionStatus(hasPendingResponse ? 'FIRST_SUBMISSION' : 'RESUBMISSION');
    } else {
      setRsvpSubmissionStatus('FIRST_SUBMISSION');
    }
  }, [guestInvitation]);

  // Create enriched events list that combines guest invitation data with website event data
  const enrichedEvents = useMemo(() => {
    if (!guestInvitation) return [];

    const invitedEventsIds = Object.keys(guestInvitation.events);

    return getEnrichedEvents(invitedEventsIds, websiteData, weddingEvents);
  }, [guestInvitation, websiteData, weddingEvents]);

  // Initialise events attendance form
  const eventsForm = useForm<IEventFormFields>();

  useEffect(() => {
    if (guestInvitation) {
      Object.entries(guestInvitation.events)
        .filter(([eventId]) => weddingEvents.map((event) => event.id).includes(eventId))
        .forEach(([eventId, { rsvp }]) => {
          eventsForm.register(eventId, {
            value: rsvp,
            validate: validateGuestRsvpEventAttendance,
            required: true,
          });
        });
    }
  }, [guestInvitation, eventsForm, weddingEvents]);

  // Initialise questions and note form
  const questionsForm = useForm<IQuestionsFormFields>({
    mode: 'onChange',
  });
  const eventsSubscriptionRef = useRef<ReturnType<typeof eventsForm.watch> | null>(null);

  // Update question registrations based on current event responses
  const updateQuestionRegistrations = useCallback(() => {
    if (!weddingRsvp?.questions) return;
    const eventResponses = eventsForm.getValues();
    const questionIdsToRegister = new Set<string>();

    weddingRsvp.questions.forEach((question) => {
      const shouldRegister = question.events.some(
        (eventId) => eventResponses[eventId] === Rsvp.YES,
      );

      if (shouldRegister) {
        questionIdsToRegister.add(question.id);
      }
    });

    // Register/unregister questions based on event responses
    weddingRsvp.questions.forEach((question) => {
      const shouldBeRegistered = questionIdsToRegister.has(question.id);

      if (shouldBeRegistered) {
        questionsForm.register(question.id, {
          validate: getGuestRsvpQuestionValidator(question, t),
          value:
            question.type === 'boolean'
              ? (guestInvitation?.questions?.[question.id]?.answer ?? '')
              : guestInvitation?.questions?.[question.id]?.answer || '',
        });
      } else if (!shouldBeRegistered) {
        questionsForm.unregister(question.id);
      }
    });
  }, [weddingRsvp, guestInvitation, eventsForm, questionsForm, t]);

  useEffect(() => {
    if (!guestInvitation || !weddingRsvp?.questions) {
      return;
    }

    // Initial registration
    updateQuestionRegistrations();

    // Register note field
    questionsForm.register('note', {
      value: guestInvitation.note,
    });

    // Clean up previous subscription if it exists
    if (eventsSubscriptionRef.current) {
      eventsSubscriptionRef.current.unsubscribe();
    }

    // Set up new subscription to watch for changes in eventsForm
    eventsSubscriptionRef.current = eventsForm.watch(updateQuestionRegistrations);

    // Cleanup subscription when component unmounts or dependencies change
    return () => {
      if (eventsSubscriptionRef.current) {
        eventsSubscriptionRef.current.unsubscribe();
        eventsSubscriptionRef.current = null;
      }
    };
  }, [guestInvitation, weddingRsvp, updateQuestionRegistrations, eventsForm, questionsForm]);

  const handleSubmit: () => Promise<{
    response: SaveGuestRsvpResponse;
  }> = async () => {
    const { note, ...answers } = questionsForm.getValues();
    const eventsAnswers = eventsForm.getValues();

    const updatedQuestions = Object.entries(answers).reduce<
      NonNullable<IGuest_Invitation['questions']>
    >((acc, [id, answer]) => {
      acc[id] = { answer };
      return acc;
    }, {});

    const updatedEvents = Object.entries(guestInvitation?.events || {}).reduce<
      IGuest_Invitation['events']
    >((acc, [eventId, event]) => {
      acc[eventId] = {
        ...event,
        rsvp: eventsAnswers[eventId] || Rsvp.PENDING,
      };
      return acc;
    }, {});

    const updateData = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: guestIdFromResponse!,
      invitation: {
        events: updatedEvents,
        note: note?.toString() || '',
        questions: updatedQuestions,
      },
    };
    return await updateGuestRsvp(updateData);
  };

  const trackGuestRsvpStarted = useCallback(() => {
    dispatch(
      clickedOnLoggedOutPage({
        clickedLocation: 'rsvpGuestView',
        clickedSection: 'findName',
        cta: 'Continue',
      }),
    );
  }, [dispatch]);

  const trackGuestRsvpSent = useCallback(() => {
    dispatch(
      clickedOnLoggedOutPage({
        clickedLocation: 'rsvpGuestView',
        clickedSection: 'sendRSVP',
        cta: 'Submit RSVP',
      }),
    );
  }, [dispatch]);

  return (
    <RsvpStateProvider
      value={{
        websiteId,
        isPreview: false,
        isRsvpOpen,
        rsvpSubmissionStatus,
        setRsvpSubmissionStatus,
      }}>
      <RsvpNavigationProvider
        value={{
          step,
          setStep,
          currentQuestionIndex,
          setCurrentQuestionIndex,
        }}>
        <RsvpFormsProvider
          value={{
            nameForm,
            eventsForm,
            questionsForm,
            handleSubmit,
          }}>
          <RsvpDataProvider
            value={{
              isLoading: isLoading || isFetching,
              error,
              guestNotFound,
              guestNoEvents,
              guestInvitation,
              weddingRsvp,
              weddingWebsite: websiteData,
              theme,
              enrichedEvents,
              relatedGuests,
            }}>
            <RsvpAnalyticsProvider
              value={{
                trackGuestRsvpStarted,
                trackGuestRsvpSent,
              }}>
              <GuestRsvpMainComponent />
            </RsvpAnalyticsProvider>
          </RsvpDataProvider>
        </RsvpFormsProvider>
      </RsvpNavigationProvider>
    </RsvpStateProvider>
  );
};

export default GuestRsvp;
